import { useEffect, useState } from 'react';
import { api } from '@/lib/api';
import type {
  SchoolProfileRow,
  SchoolProfilesParams,
  SchoolProfilesResponse,
  SchoolProfileTierFilter,
} from '@/lib/types';
import { fetchAllPages } from '@/lib/use-show-all';
import { SCHOOL_PROFILES_PER_PAGE } from './school-profiles-model';

/**
 * The School Profiles page's data layer: one read of
 * `GET /product-insights/school-profiles` per (cycle, tier, page) — or, with
 * "Show all", every page read 100 at a time and handed on as one list.
 *
 * - `cycleId` is REQUIRED upstream, so nothing is fetched until the page has
 *   resolved one (SEED FAB's cycle list, same `d_business_cycles` ids).
 * - Totals, the interaction mix, countries and sources are cycle-wide and come
 *   from the FIRST page's response; only `schools` pages.
 * - Show all goes through `fetchAllPages`, which THROWS on any failed page and
 *   never returns a partial list — a list that stops early looks complete.
 * - Previous data stays on screen while a new key loads (`refreshing`);
 *   `loading` is only true when there is nothing to show yet.
 */

export interface SchoolProfilesView {
  /** The first page's response: totals, mixes, meta. */
  response: SchoolProfilesResponse;
  /** The schools shown — one page, or every page under Show all. */
  schools: SchoolProfileRow[];
  /** Upstream `meta.total`. */
  total: number;
  page: number;
  totalPages: number;
  /** Show all stopped at its cap. */
  truncated: boolean;
}

interface Result {
  key: string;
  view: SchoolProfilesView | null;
  error: unknown | null;
}

function paramsFor(cycleId: number, tier: SchoolProfileTierFilter, page: number): SchoolProfilesParams {
  return {
    cycleId,
    page,
    perPage: SCHOOL_PROFILES_PER_PAGE,
    ...(tier === 'all' ? {} : { tier }),
  };
}

function totalPagesOf(response: SchoolProfilesResponse): number {
  return Math.max(1, response.meta.totalPages);
}

async function load(
  cycleId: number,
  tier: SchoolProfileTierFilter,
  page: number,
  showAll: boolean,
  signal: AbortSignal,
): Promise<SchoolProfilesView> {
  if (!showAll) {
    const response = await api.productInsightsSchoolProfiles(paramsFor(cycleId, tier, page), signal);
    return {
      response,
      schools: response.data.schools,
      total: response.meta.total,
      page: response.meta.page,
      totalPages: totalPagesOf(response),
      truncated: false,
    };
  }
  // A holder rather than a `let`: TS does not track an assignment made inside
  // the callback, so a bare `let` would narrow to `null` after the await.
  const holder: { first: SchoolProfilesResponse | null } = { first: null };
  const all = await fetchAllPages<SchoolProfileRow>(async (pageNumber, perPage, pageSignal) => {
    const response = await api.productInsightsSchoolProfiles(
      { ...paramsFor(cycleId, tier, pageNumber), perPage },
      pageSignal,
    );
    if (pageNumber === 1) holder.first = response;
    return {
      data: response.data.schools,
      meta: { total: response.meta.total, totalPages: totalPagesOf(response), page: response.meta.page },
    };
  }, signal);
  const first = holder.first;
  if (first === null) throw new Error('School Profiles answered no first page.');
  return { response: first, schools: all.rows, total: all.total, page: 1, totalPages: 1, truncated: all.truncated };
}

export function useSchoolProfiles(
  cycleId: number | null,
  tier: SchoolProfileTierFilter,
  page: number,
  showAll: boolean,
  ready: boolean,
  reloadToken: number,
): { view: SchoolProfilesView | null; loading: boolean; error: unknown | null; refreshing: boolean } {
  const paramsKey =
    cycleId === null || !ready ? null : `${cycleId}|${tier}|${showAll ? 'all' : page}`;
  const requestKey = paramsKey === null ? null : `${paramsKey}#${reloadToken}`;
  const [lastView, setLastView] = useState<SchoolProfilesView | null>(null);
  const [result, setResult] = useState<Result | null>(null);

  useEffect(() => {
    if (requestKey === null || cycleId === null) return;
    const controller = new AbortController();
    const { signal } = controller;
    load(cycleId, tier, page, showAll, signal)
      .then((view) => {
        if (signal.aborted) return;
        setLastView(view);
        setResult({ key: requestKey, view, error: null });
      })
      .catch((err: unknown) => {
        if (signal.aborted) return;
        setResult({ key: requestKey, view: null, error: err });
      });
    return () => controller.abort();
  }, [requestKey, cycleId, tier, page, showAll]);

  const current = result !== null && requestKey !== null && result.key === requestKey ? result : null;
  const settled = current !== null;
  return {
    view: lastView,
    loading: lastView === null && !settled,
    error: current?.error ?? null,
    refreshing: lastView !== null && !settled,
  };
}
